import { ArrowDown, ArrowUp, ListFilter, Trash2 } from 'lucide-react';

import React from 'react';

import { TColumnHeaderDropDownValue } from '@/features/form-responses/types/column';

import { ProtectedFieldLockIcon } from '@/shared/components/protected-field-lock-icon';
import { QUERY_PARAMS } from '@/shared/constants/query-params';
import { CUSTOM_COLUMNS, CUSTOM_COLUMN_TYPES } from '@/shared/constants/table-columns';
import { TSortingType } from '@/shared/services/forms/forms.types';

export const LOGIN_ROUTE = '/login';

// OAuth
export const REDIRECT_PATH = 'redirectPath';

export const GOOGLE_OAUTH_CODE_PARAM = 'code';
export const GOOGLE_OAUTH_ERROR_PARAM = 'error';
export const GOOGLE_OAUTH_STATE_PARAM = 'state';
export const GOOGLE_OAUTH_STATE_PARAM_VALUE = 'google-oauth';
export const GOOGLE_OAUTH_ERROR_ROUTE = '/google-oauth/error';

export const GOOGLE_OAUTH_ROOT_URL = `https://accounts.google.com/o/oauth2/v2/auth`;
export const GOOGLE_OAUTH_TOKEN_URL = `https://oauth2.googleapis.com/token`;
export const GOOGLE_OAUTH_CALLBACK = '/google-oauth/callback';

// Slack OAuth
export const SLACK_OAUTH_CODE_PARAM = 'code';
export const SLACK_OAUTH_ERROR_PARAM = 'error';
export const SLACK_OAUTH_STATE_PARAM = 'state';
export const SLACK_OAUTH_STATE_PARAM_VALUE = 'slack-oauth';
export const SLACK_OAUTH_ROOT_URL = 'https://slack.com/oauth/v2/authorize';
export const SLACK_OAUTH_CALLBACK = '/slack-oauth/callback';
export const SLACK_OAUTH_SCOPES =
	'app_mentions:read,channels:history,channels:read,chat:write,chat:write.public,groups:history,groups:read,im:history,im:read,reactions:write,users:read,users:read.email';

export const LOGIN_TOKEN_PARAM = 'token';

export const APP_IDENTIFIERS = ['linkedin', 'twitter'];
export const IPHONE = 'iphone';
export const INSTAGRAM = 'instagram';
export const FACEBOOK = 'facebook';
export const ANDROID = 'android';

// cookies keys
export const ACCESS_TOKEN = 'access-token';

// dashboard menu
export const HOME_DISPLAY_NAME = 'Home';
export const MESSAGES_DISPLAY_NAME = 'Inbox';
export const PROFILE_DISPLAY_NAME = 'Profile';

// form fill page search params
export const FORM_FILL_PAGE_SIGN_IN_DISABLE_SEARCH_PARAM_KEY = 'sidfss';

// form settings modal search params
export const FORM_SETTINGS_MODAL_OPEN_SEARCH_PARAM_KEY = 'settings';

// template page search params
export const TEMPLATE_PAGE_USE_TEMPLATE_SEARCH_PARAM_KEY = 'use_template';

// form widgets thresholds
export const FORM_WIDGET_APPLICATIONS_THRESHOLD = 25;
export const FORM_WIDGET_RESPONSE_RATE_THRESHOLD = 80;

export type CustomStage = 'NEW' | 'SHORTLISTED' | 'ARCHIVED';

export const FORM_DETAILS_PAGE_TABS_SEARCH_PARAMS_KEYS: QUERY_PARAMS[] = [
	QUERY_PARAMS.TAB,
	QUERY_PARAMS.FILTERS,
	QUERY_PARAMS.SEARCH,
	QUERY_PARAMS.SORT,
	QUERY_PARAMS.SUBMISSION_ID,
];

// home page tabs and search params
export const CREATION = 'created';
export const RECENCY = 'recent';
export const ALL = 'all';
export const LIVE = 'live';

export const HOME_PAGE_TABS = [ALL, LIVE];

export const HOME_PAGE_TABS_DETAILS = [
	{
		displayName: 'All',
		value: ALL,
	},
	{
		displayName: 'Live',
		value: LIVE,
	},
];

export const HOME_PAGE_TABS_VALUES_MAP: { [key: string]: TSortingType } = {
	[CREATION]: 'CREATION',
	[RECENCY]: 'RECENCY',
};

export const HOME_PAGE_TAB_SEARCH_PARAM_KEY = 'tab';

export const HOME_PAGE_TABS_SEARCH_PARAMS_KEYS = [HOME_PAGE_TAB_SEARCH_PARAM_KEY];

export const USER_FORMS_FETCH_COUNT = 10;

export const REDIRECT_TO_LOGIN_TIMEOUT = 3000;

export const FORM_SUBMISSIONS_FETCH_COUNT = 300;
export const FORM_SUBMISSIONS_PARALLEL_BATCH_SIZE = 10;

// form submissions table
export const USER_SUBMISSIONS_FETCH_COUNT = 10;

export const PRE_DEFINED_COLUMN_APPLICANT = 'applicant';
export const PRE_DEFINED_COLUMN_APPLICANT_TYPE = 'PRE_DEFINED_INPUT_APPLICANT';

export const PRE_DEFINED_COLUMN_EMAIL = 'email';
export const PRE_DEFINED_COLUMN_EMAIL_TYPE = 'PRE_DEFINED_INPUT_EMAIL';

export const PRE_DEFINED_COLUMN_DATE = 'date';
export const PRE_DEFINED_COLUMN_DATE_TYPE = 'PRE_DEFINED_INPUT_DATE';

export const PRE_DEFINED_COLUMN_MESSAGES = 'messages';
export const PRE_DEFINED_COLUMN_MESSAGES_TYPE = 'MESSAGES';

export const PRE_DEFINED_COLUMN_LABELS = 'labels';
export const PRE_DEFINED_COLUMN_LABELS_TYPE = 'SUBMISSION_LABELS';

export const PRE_DEFINED_COLUMN_AI_SHORTLISTING_TYPE = 'AI_SHORTLISTING';

/** Synthetic table column id: one column replaces all AI criteria in the responses grid */
export const COMBINED_AI_REVIEW_COLUMN_ID = '__combined_ai_review__';

// Column types that cannot have protection enabled
export const NON_PROTECTABLE_COLUMN_TYPES = [
	'PRE_DEFINED_INPUT_APPLICANT',
	'PRE_DEFINED_INPUT_EMAIL',
	'PRE_DEFINED_INPUT_DATE',
	'SUBMISSION_LABELS',
	'SUBMISSION_FEEDBACK',
	CUSTOM_COLUMN_TYPES[CUSTOM_COLUMNS.SOURCE],
	CUSTOM_COLUMN_TYPES[CUSTOM_COLUMNS.ASSIGNMENTS],
	CUSTOM_COLUMN_TYPES[CUSTOM_COLUMNS.OFFERS],
];

export const CUSTOM_FIELDS = [
	PRE_DEFINED_COLUMN_APPLICANT,
	PRE_DEFINED_COLUMN_EMAIL,
	PRE_DEFINED_COLUMN_DATE,
	PRE_DEFINED_COLUMN_LABELS,
];

/**
 * Columns that can never be deleted from the responses table — they are platform
 * columns we add, not fields anyone authored. Note `submission_feedback` is the
 * column shown in the UI as "Interviews".
 */
export const NON_DELETABLE_COLUMN_IDS = new Set<string>([
	CUSTOM_COLUMNS.APPLICANT,
	CUSTOM_COLUMNS.EMAIL,
	CUSTOM_COLUMNS.DATE,
	CUSTOM_COLUMNS.LABELS,
	CUSTOM_COLUMNS.SOURCE,
	CUSTOM_COLUMNS.ASSIGNMENTS,
	CUSTOM_COLUMNS.OFFERS,
	CUSTOM_COLUMNS.SUBMISSION_FEEDBACK,
]);

/**
 * Adding and deleting responses-table columns is built on the frontend but the
 * `/form/add-field` and `/form/delete-field` endpoints do not exist yet, so both
 * entry points stay hidden in production. Flip to `true` once backend ships them.
 *
 * Note: AI-criteria columns delete through their own existing endpoint and are
 * deliberately unaffected by this flag.
 */
export const COLUMN_MANAGEMENT_ENABLED = false;

export const NOT_AVAILABLE = '--';

export const TABLE_COLUMN_HEADER_DROPDOWN_OPTIONS: {
	value: TColumnHeaderDropDownValue;
	displayName: string;
	danger?: boolean;
	icon?: React.ElementType;
}[] = [
	{
		icon: ArrowUp,
		value: 'SORT_ASC',
		displayName: 'Asc',
	},
	{
		icon: ArrowDown,
		value: 'SORT_DESC',
		displayName: 'Desc',
	},
	{
		icon: ListFilter,
		value: 'FILTER',
		displayName: 'Filter',
	},
	{
		icon: Trash2,
		value: 'DELETE',
		displayName: 'Delete',
		danger: true,
	},
	{
		icon: ProtectedFieldLockIcon,
		value: 'PROTECT',
		displayName: 'Protect field',
	},
];

export const SELECT_FILTER_SEARCH_DISPLAY_THRESHOLD = 5;

// query keys
export const GET_USER_DETAILS_QUERY_KEY = 'getUserDetails';
export const GET_USER_FORMS_QUERY_KEY = 'getUserForms';
export const GET_FORM_DETAILS_QUERY_KEY = 'getFormDetails';
export const GET_DELETED_FIELDS_FORM_QUERY_KEY = 'getDeletedFieldsForm';
export const GET_FORM_SUBMISSIONS_QUERY_KEY = 'getFormSubmissions';
export const GET_FORM_EDIT_DETAILS_QUERY_KEY = 'getFormEditDetails';
export const GET_APPLICANT_FORM_SUBMISSIONS = 'getApplicantFormSubmissions';
export const GET_FORM_FILLING_DETAILS_QUERY_KEY = 'getFormFillingDetails';
export const FORM_FILLED_BY_USER_QUERY_KEY = 'formFilledByUser';

export const GET_CHAT_COUNTS_QUERY_KEY = 'getChatCounts';
export const GET_CHATS_QUERY_KEY = 'getChats';
export const GET_FORM_ACROSS_CHATS_QUERY_KEY = 'getFormAcrossChats';
export const GET_FORM_NOTIFICATION_SETTINGS_QUERY_KEY = 'getFormNotificationSettings';
export const GET_CURRENT_PLAN_QUERY_KEY = 'getCurrentPlan';

// quick reply replace words
export const QUICK_REPLY_REPLACE_WORDS = {
	firstName: `{first_name}`,
};

// form fields
export const LOWERCASE_ALPHANUMERIC = 'abcdefghijklmnopqrstuvwxyz0123456789';
export const FORM_FIELD_OPTION_MAX_LENGTH = 100;
export const ATTACHMENT_MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Single source of truth: file type ID → MIME type. IMPORTANT DO NOT ADD TO THIS LIST SINCE ITS THE SAME ENUM ON BACKEND AND IF WE ALTER THEN BACKEND WILL NOT BE COMPATIBLE */
export const FILE_TYPE = {
	PDF: 'application/pdf',
	JPEG: 'image/jpeg',
	JPG: 'image/jpeg',
	PNG: 'image/png',
	DOC: 'application/msword',
	DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	TXT: 'text/plain',
	CSV: 'text/csv',
	XLS: 'application/vnd.ms-excel',
	XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	PPT: 'application/vnd.ms-powerpoint',
	PPTX: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	GIF: 'image/gif',
	WEBP: 'image/webp',
	SVG: 'image/svg+xml',
	MP4: 'video/mp4',
	WEBM: 'video/webm',
	MP3: 'audio/mpeg',
	WAV: 'audio/wav',
} as const;

export type TFileTypeId = keyof typeof FILE_TYPE;

/** All valid file type IDs — use for form field validation and accept strings */
export const FILE_TYPE_IDS = Object.keys(FILE_TYPE) as TFileTypeId[]; // IMPORTANT DO NOT ADD TO THIS LIST SINCE ITS THE SAME ENUM ON BACKEND AND IF WE ALTER THEN BACKEND WILL NOT BE COMPATIBLE

export const IMAGE_FILE_IDS: TFileTypeId[] = ['JPEG', 'JPG', 'PNG', 'WEBP'];

/** Allowed MIME types for chat attachments — broader than form fields */
export const CHAT_ATTACHMENT_ALLOWED_MIME_TYPES = new Set([
	...Object.values(FILE_TYPE),
	// Archives
	'application/zip',
	'application/x-zip-compressed',
	'application/x-rar-compressed',
	'application/x-7z-compressed',

	// Text
	'text/html',
	'text/plain',

	// Data / config
	'application/json',
	'application/xml',
	'text/xml',
	'text/markdown',
	// Images
	'image/heic',
	'image/heif',
	// Audio
	'audio/aac',
	'audio/ogg',
	// Video
	'video/ogg',
	'video/quicktime',
]);

export const ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP = {
	SHORT_ANSWER: 'Short answer',
	LONG_ANSWER: 'Long answer',
	LINK: 'Any link',
	LINKEDIN: 'LinkedIn profile',
	OTHER_LINKS: 'Other links',
	SELECTION_FIELDS: 'Multiple choice',
	MULTIPLE_CHOICE: 'List',
	DROPDOWN: 'Dropdown',
	CHECKBOX: 'Multi-select',
	PHONE: 'Phone',
	NUMBER: 'Number',
	DATE: 'Date',
	FILE_UPLOAD: 'File upload',
	VIDEO: 'Video recording',
	RATING: 'Rating',
	TWITTER: 'X (Twitter)',
	INSTAGRAM: 'Instagram',
	GITHUB: 'Github',
	YOUTUBE: 'YouTube video',
	RICH_TEXT: 'Rich text',
};

export const ADD_FIELD_OPTIONS_NAMES_MAP_WITH_INPUT_AND_PRE_DEFINED_INPUT_TYPE: {
	[key: string]: string;
} = {
	SHORT_TEXT: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.SHORT_ANSWER,
	LONG_TEXT: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.LONG_ANSWER,
	NUMBER: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.NUMBER,
	DATE: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.DATE,
	SINGLE_SELECT: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.MULTIPLE_CHOICE,
	MULTI_SELECT: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.CHECKBOX,
	DROPDOWN: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.DROPDOWN,
	FILE_UPLOAD: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.FILE_UPLOAD,
	VIDEO: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.VIDEO,
	RATING: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.RATING,
	LINK: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.LINK,
	PHONE_NUMBER: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.PHONE,
	LINKEDIN: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.LINKEDIN,
	OTHER_LINKS: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.OTHER_LINKS,
	TWITTER: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.TWITTER,
	INSTAGRAM: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.INSTAGRAM,
	GITHUB: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.GITHUB,
	YOUTUBE: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.YOUTUBE,
	RICH_TEXT: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.RICH_TEXT,
};

export const NON_EDITABLE_INPUT_FIELD_TYPES = ['NAME', 'EMAIL'];
export const DEFAULT_ADD_OPTION_NAME = 'Add an option';
export const FORM_FIELD_DESCRIPTION_ID = 'form-field-description';
export const FORM_FIELD_TITLE_ID = 'form-field-title';

// form edit layout
export const FORM_EDIT_LAYOUT_GRID_ID = 'form-edit-layout-grid';

// messages page
export const MESSAGES_PAGE_TAB_SEARCH_PARAM_KEY = 'tab';
export const MESSAGES_PAGE_STATUS_SEARCH_PARAM_KEY = 'status';
export const MESSAGES_PAGE_CHAT_SEARCH_PARAM_KEY = 'chatId';
export const MESSAGES_PAGE_USER_VIEW_SEARCH_PARAM_KEY = 'view';

export const MESSAGES_PAGE_STATUS_OPTIONS = {
	ALL: 'all',
	UNREAD: 'unread',
};

export const MESSAGES_PAGE_TABS_OPTIONS = {
	PRIMARY: 'primary',
	ARCHIVED: 'archived',
};
export type TimeInterval = 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'LAST_45_DAYS' | 'LAST_90_DAYS';

export const CHAT_MESSAGE_VIEW_AS = {
	PAGE_USER: {
		urlParam: 'editor',
		requestParam: 'PAGE_USER',
	},
	SUBMITTER: {
		urlParam: 'applicant',
		requestParam: 'SUBMITTER',
	},
};

export const CHAT_MESSAGE_SENDER_TYPE = {
	PAGE_USER: {
		urlParam: 'editor',
		requestParam: 'PAGE_USER',
	},
	SUBMITTER: {
		urlParam: 'applicant',
		requestParam: 'SUBMITTER',
	},
};

export const LABEL_COLORS = [
	'#A9A9A9', // Grey
	'#C2410C', // Orange
	'#4CB782', // Green
	'#F2C94C', // Yellow
	'#EB5757', // Red
] as const;

export const COLOR_NAMES: Record<string, string> = {
	'#A9A9A9': 'Grey',
	'#C2410C': 'Orange',
	'#4CB782': 'Green',
	'#F2C94C': 'Yellow',
	'#EB5757': 'Red',
};

export const CHECKOUT_SESSION_QUERY_KEY = 'checkouts';
export const VERIFY_PAYMENT_QUERY_KEY = 'verify-payment';
export const PAYMENT_SESSION_ID_QUERY_PARAM = 'session_id';

export const PAYMENT_STATUS_OPTIONS = {
	COMPLETE: 'complete',
	EXPIRED: 'expired',
	OPEN: 'open',
};

const veryHighZIndex = 99999999;
export const Z_INDEXES = {
	// Must stay below the Radix portal layer (z-50) so dialogs/sheets/popovers cover the sidebar.
	SIDEBAR: 40,
	OVERLAY_SHEET: veryHighZIndex,
	TIPPY: veryHighZIndex,
	FORM_USERS_DIALOG: veryHighZIndex + 10,
	TOOLTIP: veryHighZIndex + 11,
	ALERT_ACTION_DIALOG: veryHighZIndex + 20,
};

export const UNKNOWN_LABEL_VALUE = 'UN_LABELED';
export const CLEAR_ALL_FILTER_PLACEHOLDER_VALUE = 'CLEAR_ALL_VALUE__PLACEHOLDER_EMPTY_FILTER';

export const BLANK_RECORD_VALUE = 'BLANK_RECORD_VALUE$@!%-';
export const OTHER_OPTION_ID = 'OTHER_OPTION$@!%-';
